import {
  Button,
  InputBase,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Product, Task } from "../../../services/swagger";
import { TaskService } from "../../../services/api";
import { useTranslation } from "react-i18next";
import { useState, useTransition } from "react";
import ProductSelectorDialog from "../../organisms/Product/ProductSelectorDialog";
import IconAvatar from "../../atoms/IconAvatar";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import useRefresh from "../../../hooks/useRefresh";
import ProductSerialSelector from "../ProductSerialSelector";
import useConfirm from "../../../hooks/useConfirm";
import TaskProductSerialList from "./TaskProductSerialList";
import VisibilityIcon from "@mui/icons-material/Visibility";

interface TaskProductListProps {
  task: Task;
}

export default function TaskProductList(props: TaskProductListProps) {
  const { task } = props;
  const { t } = useTranslation("Product");
  const [isPending, startTransition] = useTransition();
  const [productsDialog, setProductsDialog] = useState(false);
  const [serialDialog, setSerialDialog] = useState(false);

  const confirm = useConfirm();

  const refresh = useRefresh();

  const handleOpenProductsDialog = () => {
    setProductsDialog(true);
  };

  const handleCloseSerialDialog = () => {
    setSerialDialog(false);
  };

  const handleCloseProductsDialog = () => {
    setProductsDialog(false);
  };

  const handleSelectNewProducts = async (products: Product[]) => {
    if (!task._id) return;
    const res = await TaskService.createTaskProduct({
      id: task._id,
      createTaskProductBody: {
        products: products.map((p) => ({
          itemCode: p.itemCode,
          name: p.name,
          productRef: p._id || "",
          quantity: 1,
          serialList: [],
        })),
      },
    });

    if (res?.data?.success) {
      handleCloseProductsDialog();
      refresh();
    }
  };

  const handleDeleteProduct = (itemCode: string) => {
    confirm(async () => {
      if (!task._id) return;
      const res = await TaskService.deleteTaskProduct({
        id: task._id,
        itemCode,
      });
      if (res?.data?.success) refresh();
    });
  };

  const handleUpdateProductQuantity = (
    itemCode: string,
    newQuantity: string
  ) => {
    if (!newQuantity.match(/^[1-9]\d*$/)) return;
    const index = task.products.findIndex((p) => p.itemCode === itemCode);
    if (index < 0) return;
    task.products[index].quantity = parseInt(newQuantity);
  };

  return (
    <Stack>
      <ProductSelectorDialog
        open={productsDialog}
        onClose={handleCloseProductsDialog}
        onSelect={handleSelectNewProducts}
        ignoreCodes={task.products.map((product) => product.itemCode)}
      />
      <Button
        onClick={handleOpenProductsDialog}
        variant="text"
        sx={{ textTransform: "none", mb: 1 }}
        fullWidth
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <Typography fontWeight={400} fontSize={14} color="primary">
            {t("ProductsUsed")}
          </Typography>

          <AddIcon fontSize={"small"} sx={{ color: "grey" }} />
        </Stack>
      </Button>
      <TaskProductSerialList
        task={props.task}
        open={serialDialog}
        onClose={handleCloseSerialDialog}
      />
      {task.products && task.products.length > 0 ? (
        <TableContainer sx={{ maxHeight: { md: 200 } }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ px: 1 }}>{t("ItemCode")}</TableCell>
                <TableCell>{t("Name")}</TableCell>
                <TableCell width={80}>{t("Quantity")}</TableCell>
                <TableCell width={10} sx={{ pr: 1 }}>
                  <Stack
                    sx={{
                      mb: 0.25,
                      fontSize: "inherit",
                      fontWeight: "inherit",
                      WebkitUserSelect: "none",
                      userSelect: "none",
                      ":hover": {
                        color: "primary.main",
                        cursor: "pointer",
                      },
                    }}
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    onClick={() => setSerialDialog(true)}
                  >
                    <Typography fontWeight="inherit" fontSize="inherit">
                      {t("Serials")}
                    </Typography>
                    <VisibilityIcon sx={{ fontSize: 14 }} />
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {task.products.map((taskProduct) => (
                <TableRow key={taskProduct.itemCode} hover>
                  <TableCell sx={{ px: 1 }}>{taskProduct.itemCode}</TableCell>
                  <TableCell>{taskProduct.name}</TableCell>
                  <TableCell width={80}>
                    <InputBase
                      defaultValue={taskProduct.quantity}
                      type="number"
                      sx={{ p: 0 }}
                      onChange={(e) =>
                        handleUpdateProductQuantity(
                          taskProduct.itemCode,
                          e.target.value
                        )
                      }
                    />
                  </TableCell>
                  <TableCell width={10} sx={{ px: 1 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ProductSerialSelector
                        task={task}
                        taskProduct={taskProduct}
                      />
                      <IconAvatar
                        icon={CloseIcon}
                        size={18}
                        onClick={() =>
                          handleDeleteProduct(taskProduct.itemCode)
                        }
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography fontSize={11} pl={1} fontWeight={400} color="textSecondary">
          {t("NoItemsYet")}
        </Typography>
      )}
    </Stack>
  );
}
